/**
 * P4.T9 deterministic rule engine.
 *
 * Pure function over PersonaBundle + catalogs (capabilities, adapters,
 * skills, workflows). Returns Finding[] for the Workshop's Review tab
 * validation (traffic-light icons + bundle-wide findings panel).
 *
 * Eight rules ship in v1 (rule 7 from spec dropped — autostart_on_channel
 * is non-nullable, see §5 entry 2026-05-22). See
 * `docs/superpowers/specs/2026-05-22-p4-t9-explain-why.md` §4 for the
 * canonical rule definitions.
 */
import type { PersonaBundleResponse } from '@/types/personaWizard'
import type { Capability, Adapter } from '@/types/toolFramework'
import type { SkillCatalogEntry } from '@/hooks/useSkillCatalog'
import type { WorkflowSummary } from '@/hooks/useWorkflows'
import type { WorkflowBindingValue } from '@/stores/composerStore'

export type Severity = 'error' | 'warning'

export type FindingLocation =
  | { card: 'skills';    anchorId: string }
  | { card: 'tools';     anchorId: string }
  | { card: 'workflows'; anchorId: string }
  | { card: 'identity' }
  | { card: 'bundle' }

export interface Finding {
  id: string
  rule: string
  severity: Severity
  location: FindingLocation
  message: string
  explanation: string[]
}

interface Catalogs {
  capabilities: Capability[]
  adapters: Adapter[]
  skills: SkillCatalogEntry[]
  workflows: WorkflowSummary[]
}

/** What the rules read beyond the bundle and the catalogs (SA-474). */
export interface FindingsContext {
  /** The deployment routes web chat through workflows; absent reads as on. */
  workflowEngine?: boolean
  /** The persona's saved `voice_config.speech_mode`. */
  speechMode?: string | null
}

/** The channels whose bindings answer web chat (`workflow_routing._find_matching_binding`). */
const WEB_CHAT_CHANNELS = new Set(['*', 'chat'])

interface ToolBindingValue {
  adapter_id: string
  credential_set_id?: string | null
  policy_overrides?: Record<string, unknown>
}

const CARD_ORDER: Record<FindingLocation['card'], number> = {
  bundle: 0, identity: 1, skills: 2, tools: 3, workflows: 4,
}

export function computeBundleFindings(
  bundle: PersonaBundleResponse | null,
  catalogs: Catalogs,
  context: FindingsContext = {},
): Finding[] {
  if (!bundle) {
    return [bundleEmptyFinding()]
  }
  const blocks = bundle.blocks ?? []
  const toolBindings = (bundle.tool_bindings ?? {}) as Record<string, ToolBindingValue>
  const workflowBindingsList = (bundle.workflow_bindings ?? []) as WorkflowBindingValue[]
  if (blocks.length === 0 && Object.keys(toolBindings).length === 0 && workflowBindingsList.length === 0) {
    return [bundleEmptyFinding()]
  }

  const adapterById = new Map(catalogs.adapters.map(a => [a.id, a]))
  const skillByName = new Map(catalogs.skills.map(s => [s.name, s]))
  const workflowById = new Map(catalogs.workflows.map(w => [w.id, w]))
  const capabilityById = new Map(catalogs.capabilities.map((c) => [c.id, c]))
  const knownCapabilityIds = new Set(capabilityById.keys())

  // A capability resolves at runtime WITHOUT an explicit persona binding
  // when the platform supplies a default: a recommended_default_adapter
  // (binding_resolver.resolve() falls back to it) or a platform-internal
  // capability (classification "internal", dispatched by a built-in handler
  // before binding resolution). Mirrors the backend resolution precedence —
  // such capabilities must not be flagged "unbound".
  const resolvesWithoutBinding = (capId: string): boolean => {
    const cap = capabilityById.get(capId)
    if (!cap) return false
    if (cap.recommended_default_adapter) return true
    return (cap.classifications ?? []).includes('internal')
  }

  const installedSkillNames = new Set<string>([
    ...(bundle.default_skills ?? []).map((s) => s.name),
    ...(bundle.auto_skills ?? []).map((s) => s.name),
  ])
  const installedSkills = Array.from(installedSkillNames)
    .map(name => skillByName.get(name))
    .filter((s): s is SkillCatalogEntry => !!s)

  const installedWorkflows = workflowBindingsList
    .map(b => workflowById.get(b.workflow_id))
    .filter((w): w is WorkflowSummary => !!w)

  const usedCapabilities = new Set<string>()
  for (const s of installedSkills) {
    for (const cap of s.required_capabilities ?? []) usedCapabilities.add(cap)
  }
  for (const w of installedWorkflows) {
    for (const cap of w.required_capabilities ?? []) usedCapabilities.add(cap)
  }

  const findings: Finding[] = []

  // Installed skill names that aren't in the catalog at all → removable.
  for (const name of installedSkillNames) {
    if (!skillByName.has(name)) {
      findings.push({
        id: `skill:${name}:not_in_catalog`,
        rule: 'skill_not_in_catalog',
        severity: 'warning',
        location: { card: 'skills', anchorId: name },
        message: `"${name}" is installed but no longer exists`,
        explanation: [
          `This persona installs the skill "${name}", but it is not in the skill catalog.`,
          `It was likely removed or superseded by a workflow.`,
          `Remove it from this persona's skills.`,
        ],
      })
    }
  }

  for (const skill of installedSkills) {
    const unknownCaps: string[] = []
    for (const cap of skill.required_capabilities ?? []) {
      if (!knownCapabilityIds.has(cap)) {
        unknownCaps.push(cap)
        continue
      }
      const binding = toolBindings[cap]
      if (!binding) {
        if (resolvesWithoutBinding(cap)) continue
        findings.push({
          id: `skill:${skill.name}:requires:${cap}`,
          rule: 'skill_unsatisfied_capability',
          severity: 'error',
          location: { card: 'skills', anchorId: skill.name },
          message: `${skill.display_name || skill.name} requires ${cap} but no binding exists`,
          explanation: [
            `Skill "${skill.display_name || skill.name}" declares required_capabilities: [${(skill.required_capabilities ?? []).join(', ')}].`,
            `Capability "${cap}" is not bound in tool_bindings.`,
            `Bind ${cap} on the Tools card to satisfy this requirement.`,
          ],
        })
      } else if (!adapterById.has(binding.adapter_id)) {
        findings.push({
          id: `skill:${skill.name}:requires:${cap}:adapter_unknown`,
          rule: 'skill_bound_to_unregistered_adapter',
          severity: 'error',
          location: { card: 'skills', anchorId: skill.name },
          message: `${skill.display_name || skill.name} → ${cap} bound to ${binding.adapter_id} (not registered)`,
          explanation: [
            `Skill "${skill.display_name || skill.name}" requires capability "${cap}".`,
            `tool_bindings["${cap}"].adapter_id is "${binding.adapter_id}".`,
            `"${binding.adapter_id}" is not in the adapter registry — runtime calls will fail.`,
            `Rebind ${cap} to a registered adapter on the Tools card.`,
          ],
        })
      }
    }
    if (unknownCaps.length > 0) {
      findings.push({
        id: `skill:${skill.name}:obsolete_capabilities`,
        rule: 'skill_requires_unknown_capability',
        severity: 'warning',
        location: { card: 'skills', anchorId: skill.name },
        message: `${skill.display_name || skill.name} needs capabilities that no longer exist (${unknownCaps.join(', ')})`,
        explanation: [
          `Skill "${skill.display_name || skill.name}" declares required_capabilities not in the platform catalog: ${unknownCaps.join(', ')}.`,
          `These capabilities were removed or renamed — the skill has likely been superseded (e.g. by a workflow).`,
          `Remove this skill, or update it to use current capabilities.`,
        ],
      })
    }
  }

  for (const [cap, binding] of Object.entries(toolBindings)) {
    const adapter = adapterById.get(binding.adapter_id)
    if (!adapter) {
      findings.push({
        id: `binding:${cap}:adapter_unknown`,
        rule: 'binding_adapter_unregistered',
        severity: 'error',
        location: { card: 'tools', anchorId: cap },
        message: `Adapter "${binding.adapter_id}" is not registered`,
        explanation: [
          `tool_bindings["${cap}"].adapter_id is "${binding.adapter_id}".`,
          `"${binding.adapter_id}" is not in the adapter registry.`,
          `Rebind to a registered adapter, or install the missing adapter via /admin/tools.`,
        ],
      })
    } else if ((binding.credential_set_id == null) && adapter.kind === 'mcp') {
      findings.push({
        id: `binding:${cap}:credential_set_missing`,
        rule: 'binding_missing_credential_set',
        severity: 'warning',
        location: { card: 'tools', anchorId: cap },
        message: `MCP adapter "${adapter.display_name}" has no credential set`,
        explanation: [
          `tool_bindings["${cap}"].credential_set_id is unset.`,
          `Adapter "${adapter.display_name}" is an MCP adapter — typically needs credentials.`,
          `The runtime will fall back to the workspace default credential set, which may not exist.`,
          `Configure a credential set via /admin/credential-sets.`,
        ],
      })
    }
    // Rule 5 (orphaned binding) fires independently of rules 3 + 4.
    // A binding to an unregistered adapter for an unused capability surfaces
    // BOTH rule 3 (adapter broken) AND rule 5 (nothing uses this) — two
    // complementary findings on the same row. Spec §4 rule 5 has no
    // dependency on rule 3.
    if (!usedCapabilities.has(cap)) {
      findings.push({
        id: `binding:${cap}:orphaned`,
        rule: 'binding_orphaned',
        severity: 'warning',
        location: { card: 'tools', anchorId: cap },
        message: `${cap} is bound but no installed skill or workflow uses it`,
        explanation: [
          `tool_bindings["${cap}"] is set, but...`,
          `No installed skill declares ${cap} in required_capabilities.`,
          `No installed workflow references ${cap}.`,
          `Either install a skill/workflow that uses it, or remove this binding.`,
        ],
      })
    }
  }

  for (const wf of installedWorkflows) {
    const unknownCaps: string[] = []
    for (const cap of wf.required_capabilities ?? []) {
      if (!knownCapabilityIds.has(cap)) {
        unknownCaps.push(cap)
        continue
      }
      if (!(cap in toolBindings)) {
        if (resolvesWithoutBinding(cap)) continue
        findings.push({
          id: `workflow:${wf.id}:requires:${cap}`,
          rule: 'workflow_uses_unbound_capability',
          severity: 'error',
          location: { card: 'workflows', anchorId: wf.id },
          message: `Workflow "${wf.name}" needs ${cap} but it is not bound`,
          explanation: [
            `Workflow "${wf.name}" requires capabilities: [${(wf.required_capabilities ?? []).join(', ')}].`,
            `Capability "${cap}" is not bound in tool_bindings.`,
            `Bind ${cap} on the Tools card to enable this workflow.`,
          ],
        })
      }
    }
    if (unknownCaps.length > 0) {
      findings.push({
        id: `workflow:${wf.id}:obsolete_capabilities`,
        rule: 'workflow_requires_unknown_capability',
        severity: 'warning',
        location: { card: 'workflows', anchorId: wf.id },
        message: `Workflow "${wf.name}" references capabilities that no longer exist (${unknownCaps.join(', ')})`,
        explanation: [
          `Workflow "${wf.name}" declares required_capabilities not in the platform catalog: ${unknownCaps.join(', ')}.`,
          `These were removed or renamed. Update the workflow definition or ignore if intentional.`,
        ],
      })
    }
  }

  // SA-474: the first binding on `*` or `chat` answers every web-chat turn,
  // as the router takes the first binding that matches the channel.
  const takeover = workflowBindingsList.find((b) => WEB_CHAT_CHANNELS.has(b.autostart_on_channel))
  if (takeover && context.workflowEngine !== false) {
    const workflow = workflowById.get(takeover.workflow_id)
    const name = workflow?.name ?? takeover.workflow_id
    const mode = context.speechMode
    findings.push({
      id: `workflow:${takeover.workflow_id}:takes_over_web_chat`,
      rule: 'workflow_takes_over_web_chat',
      severity: 'warning',
      location: { card: 'workflows', anchorId: takeover.workflow_id },
      message: `"${name}" answers every web-chat turn on this persona`,
      explanation: [
        `This binding's channel is "${takeover.autostart_on_channel}", which includes web chat.`,
        `Every web-chat message runs the workflow instead of the persona's normal answer.`,
        `Those replies show no sources and arrive all at once instead of streaming.`,
        mode && mode !== 'verbatim'
          ? `This persona's speech mode is "${mode}", but workflow replies ignore it: voice reads the whole reply aloud.`
          : `Workflow replies ignore the persona's speech mode.`,
        ...(workflow
          ? []
          : [`This workflow is not in the catalog. New conversations will not start it, but conversations it already started keep running it.`]),
        `If web chat should give the normal answer, set the channel to whatsapp or voice, or remove the binding.`,
      ],
    })
  }

  if (blocks.length > 0 && !blocks.some((b) => b.category === 'identity')) {
    findings.push({
      id: 'identity:missing_block',
      rule: 'identity_empty_required_block',
      severity: 'warning',
      location: { card: 'identity' },
      message: 'No identity block — persona lacks a role/voice definition',
      explanation: [
        `bundle.blocks contains no block with category="identity".`,
        `Identity is the foundational prose block — without it, the persona has no role, voice, or domain definition.`,
        `Add an identity block on the Identity card.`,
      ],
    })
  }

  findings.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'error' ? -1 : 1
    if (a.location.card !== b.location.card) return CARD_ORDER[a.location.card] - CARD_ORDER[b.location.card]
    const aAnchor = 'anchorId' in a.location ? a.location.anchorId : ''
    const bAnchor = 'anchorId' in b.location ? b.location.anchorId : ''
    return aAnchor.localeCompare(bAnchor)
  })

  return findings
}

function bundleEmptyFinding(): Finding {
  return {
    id: 'bundle:empty',
    rule: 'bundle_empty',
    severity: 'warning',
    location: { card: 'bundle' },
    message: 'This persona bundle has no content yet',
    explanation: [
      `bundle.blocks is empty AND no tool bindings AND no workflow bindings.`,
      `Start by adding an identity block on the Identity card and installing skills.`,
    ],
  }
}
